import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtMoney, fmtNum, fmtPct } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine,
} from "recharts";
import { Globe2, Loader2, Sparkles, ShieldAlert, CheckCircle2, Languages } from "lucide-react";
import StatTable from "@/components/StatTable";

const tooltipStyle = { backgroundColor: "#14181B", border: "1px solid #242B30", borderRadius: "10px", fontFamily: "JetBrains Mono", fontSize: "12px", color: "#F4F4F5" };

export default function Markets() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/markets");
      setData(res.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAi = async () => {
    setAiLoading(true); setAi(null);
    try {
      const res = await api.post("/ai/markets");
      setAi(res.data.analysis);
      toast.success("Market analysis ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAiLoading(false); }
  };

  const rows = data?.by_country || [];
  const chartData = [...rows].sort((a, b) => b.spend - a.spend);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-zinc-400">Market comparison uses available performance data only. Do not expand into a new country without first verifying <span className="text-zinc-200">service/freelancer availability</span> and <span className="text-zinc-200">data quality</span>.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 text-lime-400 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-24 bg-[#14181B] border border-[#242B30] rounded-xl text-zinc-400">No market data available.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-4 sm:p-5">
              <h3 className="font-display font-semibold text-white mb-1">Spend by Market</h3>
              <p className="text-[11px] text-zinc-500 font-mono mb-4">Distribution across countries</p>
              <div className="h-[240px] sm:h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1B2226" vertical={false} />
                    <XAxis dataKey="country_name" stroke="#3f4b52" fontSize={10} fontFamily="JetBrains Mono" angle={-20} textAnchor="end" height={50} interval={0} />
                    <YAxis stroke="#3f4b52" fontSize={11} fontFamily="JetBrains Mono" />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(163,230,53,0.05)" }} />
                    <Bar dataKey="spend" radius={[4, 4, 0, 0]} name="Spend">
                      {chartData.map((_, i) => <Cell key={i} fill="#A3E635" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-4 sm:p-5">
              <h3 className="font-display font-semibold text-white mb-1">CPM vs KARYOW Average</h3>
              <p className="text-[11px] text-zinc-500 font-mono mb-4">Avg CPM: {fmtMoney(data.avg_cpm, cur)}</p>
              <div className="h-[240px] sm:h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1B2226" vertical={false} />
                    <XAxis dataKey="country_name" stroke="#3f4b52" fontSize={10} fontFamily="JetBrains Mono" angle={-20} textAnchor="end" height={50} interval={0} />
                    <YAxis stroke="#3f4b52" fontSize={11} fontFamily="JetBrains Mono" />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(163,230,53,0.05)" }} />
                    <ReferenceLine y={data.avg_cpm} stroke="#F59E0B" strokeDasharray="4 4" />
                    <Bar dataKey="cpm" radius={[4, 4, 0, 0]} name="CPM">
                      {chartData.map((r, i) => <Cell key={i} fill={r.cpm > data.avg_cpm ? "#F43F5E" : "#10B981"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="bg-[#14181B] border border-[#242B30] rounded-xl overflow-hidden">
            <div className="p-5 border-b border-[#242B30]"><h3 className="font-display font-semibold text-white">Country Comparison Matrix</h3></div>
            <div className="overflow-x-auto">
              <StatTable
                testIdPrefix="market"
                rowKey={(r) => r.country_name}
                rows={chartData}
                columns={[
                  { key: "country_name", label: "Country", primary: true },
                  { key: "spend", label: "Spend", render: (r) => fmtMoney(r.spend, cur) },
                  { key: "reach", label: "Reach", render: (r) => fmtNum(r.reach) },
                  { key: "impressions", label: "Impr.", render: (r) => fmtNum(r.impressions) },
                  { key: "cpm", label: "CPM", render: (r) => fmtMoney(r.cpm, cur),
                    accent: (r) => (r.cpm > data.avg_cpm ? "text-rose-400" : "text-lime-400") },
                  { key: "ctr", label: "CTR", render: (r) => fmtPct(r.ctr) },
                  { key: "cpc", label: "CPC", render: (r) => fmtMoney(r.cpc, cur) },
                  { key: "website_visits", label: "Visits", render: (r) => fmtNum(r.website_visits) },
                  { key: "conversions", label: "Conv.", render: (r) => fmtNum(r.conversions) },
                ]}
              />
            </div>
          </div>

          <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
              <div className="flex items-center gap-2">
                <Globe2 className="h-4 w-4 text-lime-400" />
                <h3 className="font-display font-semibold text-white">AI Market Intelligence</h3>
              </div>
              <button data-testid="market-ai-btn" onClick={runAi} disabled={aiLoading}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 min-h-[44px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Analyze Markets
              </button>
            </div>
            {ai && (
              <div className="mt-4 space-y-4">
                <p className="text-zinc-200">{ai.summary}</p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-lime-400 mb-2 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Opportunities</div>
                    <div className="space-y-2">{(ai.opportunities || []).map((o, i) => (
                      <div key={i} className="bg-[#0F1315] border border-[#242B30] rounded-lg p-3">
                        <div className="text-sm text-white">{o.country}</div>
                        <div className="text-[11px] text-zinc-400 mt-1">{o.reason}</div>
                        <div className="text-[11px] text-amber-400/80 mt-1 font-mono">⚠ {o.caveat}</div>
                      </div>
                    ))}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-rose-400 mb-2 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Watchouts</div>
                    <div className="space-y-2">{(ai.watchouts || []).map((o, i) => (
                      <div key={i} className="bg-[#0F1315] border border-[#242B30] rounded-lg p-3">
                        <div className="text-sm text-white">{o.country}</div>
                        <div className="text-[11px] text-zinc-400 mt-1">{o.reason}</div>
                      </div>
                    ))}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-cyan-400 mb-2 flex items-center gap-1"><Languages className="h-3 w-3" /> Localized Angles</div>
                    <div className="space-y-2">{(ai.localized_angles || []).map((o, i) => (
                      <div key={i} className="bg-[#0F1315] border border-[#242B30] rounded-lg p-3">
                        <div className="text-sm text-white">{o.country}</div>
                        <div className="text-[11px] text-zinc-400 mt-1">{o.angle}</div>
                      </div>
                    ))}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
